import React, { ReactNode } from 'react';
import { Discriminate } from '@/utils/Utils';

export type DropdownItemData = {
    title: string | ReactNode;
    icon?: string;
    subtitle?: string;
    onClick?: () => void | Promise<void>;
    header?: boolean;
    category?: string;
    disabled?: boolean;
    searchText?: string;
    keepDropdownOpen?: boolean;
    pendingText?: string;
};

export const getOptionId = (index: number) => `search-option-${index}`;

type DropdownItemProps = {
    focusIndex: number;
    index: number;
    buttonClassName: string;
    itemClassName?: string;
    clickHandler?: (item: DropdownItemData) => void | Promise<void>;
} & Discriminate<
    { item: DropdownItemData },
    { icon?: string; title: string; subtitle: string; disabled?: boolean }
>;

const DropdownItem = ({
    clickHandler,
    focusIndex,
    index,
    itemClassName = '',
    buttonClassName,
    title,
    subtitle,
    icon,
    item,
    disabled,
}: DropdownItemProps) => {
    const isDisabled = item ? item.disabled : disabled;
    const isActive = focusIndex === index;
    const isHeader = !!item?.header;
    const onClick = clickHandler && item ? () => clickHandler(item) : item?.onClick;

    if (isHeader) {
        return (
            <li className={itemClassName} role="presentation">
                <span className={` ${buttonClassName}`} aria-hidden="true">
                    <span className="item-label">{item ? item.title : title}</span>
                    <span className="item-info">{item ? item.subtitle : subtitle}</span>
                </span>
            </li>
        );
    }

    return (
        <li
            className={itemClassName}
            role="option"
            id={getOptionId(index)}
            aria-selected={isActive}
            aria-disabled={isDisabled || undefined}
        >
            <button
                disabled={isDisabled}
                tabIndex={-1}
                type="button"
                onClick={onClick}
                id={item?.title?.toString()}
                className={`item ${buttonClassName} ${isActive ? 'active' : ''}`}
            >
                <i
                    className={`${item ? item.icon : icon} dropdown-icon`}
                    aria-hidden="true"
                />
                {item?.title && typeof item.title !== 'string' ? (
                    item?.title
                ) : (
                    <span className="item-label">{item ? item.title : title}</span>
                )}
                <span className="item-info">{item ? item.subtitle : subtitle}</span>
            </button>
        </li>
    );
};

export default DropdownItem;
